import { useLocation } from "react-router-dom";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { FooterButton } from "./FooterButton";
import { ChallengesIcon } from "@components/Icons/Challenges";
import { HighscoresIcon } from "@components/Icons/Highscores";
import { UploadIcon } from "@components/Icons/Upload";
import { FC, useEffect } from "react";
import { GalleryIcon } from "@components/Icons/Gallery";
import { CalendarIcon } from "@components/Icons/CalendarIcon";

type Props = {
  hideUpload?: boolean;
};

// Which buttons the footer shows depends on the event config. Switching tabs
// remounts the Footer, so `useEvent()` reports `undefined` for a render while it
// re-subscribes — during which the button set briefly changes (e.g. the gallery
// button drops, reflowing the whole bar). We remember the last-known config per
// event and fall back to it during that gap so the footer stays stable. Scope is
// the footer only; the worst case is a button lagging one navigation behind an
// admin config change before the live snapshot corrects it.
type FooterFlags = {
  showGuestGalleryTab: boolean;
  hideHighscores: boolean;
  isPromo: boolean;
};

const footerConfigCache = new Map<string, FooterFlags>();

export const Footer: FC<Props> = ({ hideUpload }) => {
  const location = useLocation();
  const eventId = useEventId();
  const [event] = useEvent();

  const liveFlags: FooterFlags | undefined = event && {
    // event?.showGuestGalleryTab is deprecated
    showGuestGalleryTab: event.mainConfig?.showGuestGalleryTab ?? event.showGuestGalleryTab ?? false,
    hideHighscores: event.mainConfig?.hideHighscores ?? false,
    isPromo: event.isPromo ?? false,
  };

  useEffect(() => {
    if (eventId && liveFlags) footerConfigCache.set(eventId, liveFlags);
  }, [eventId, liveFlags?.showGuestGalleryTab, liveFlags?.hideHighscores, liveFlags?.isPromo]);

  // Fall back to the last-known config for this event while it re-loads on tab change.
  const flags = liveFlags ?? (eventId ? footerConfigCache.get(eventId) : undefined);

  const showGuestGalleryTab = flags?.showGuestGalleryTab ?? false;
  const hideHighscores = flags?.hideHighscores ?? false;

  const highscoresSelected = location.pathname.includes("highscores");
  const challengesSelected = location.pathname.includes("challenges");
  const uploadSelected = location.pathname.includes("upload");
  const guestGallerySelected = location.pathname.includes("guest-gallery");
  const eventAgendaSelected = location.pathname.includes("agenda");

  // Temporary code - onninen event - remove later
  const isOnninen = eventId === "onninen";

  // Onninen is treated as a promo event UX-wise (no direct upload) even when isPromo is unset on its event doc.
  const isPromoEvent = (flags?.isPromo ?? false) || isOnninen;

  // Show event programme (agenda)
  const isUrbostogos = eventId === "urbostogos";
  const showEventAgenda = isUrbostogos;

  return (
    <footer className="border-grey bottom-0 z-50 h-16 w-full justify-center border-t-0 px-4 pb-4 text-center">
      <div className="flex h-16 justify-center">
        <div className="btm-nav btm-nav-md">
          <FooterButton active={challengesSelected} href={`/${eventId}/challenges`} data-testid="challenges-footer-btn">
            <ChallengesIcon />
          </FooterButton>
          {!isPromoEvent && (
            <FooterButton
              disabled={hideUpload}
              active={uploadSelected}
              href={`/${eventId}/upload`}
              data-testid="upload-footer-btn"
            >
              <UploadIcon />
            </FooterButton>
          )}
          {showGuestGalleryTab && (
            <FooterButton
              active={guestGallerySelected}
              href={`/${eventId}/guest-gallery`}
              data-testid="guest-gallery-footer-btn"
            >
              <GalleryIcon />
            </FooterButton>
          )}
          {!hideHighscores && (
            <FooterButton
              active={highscoresSelected}
              href={`/${eventId}/highscores`}
              data-testid="highscores-footer-btn"
            >
              <HighscoresIcon />
            </FooterButton>
          )}
          {showEventAgenda && (
            <FooterButton
              active={eventAgendaSelected}
              href={`/${eventId}/agenda`}
              data-testid="event-agenda-footer-btn"
            >
              <CalendarIcon />
            </FooterButton>
          )}
        </div>
      </div>
    </footer>
  );
};
